import React, { useContext, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  CONNECT,
  GOT_MULTIBAG_PRIZE,
  GOT_TOURNAMENT_REBUY,
  GOT_TOURNAMENTS,
  LOBBY_DATA_RECEIVED,
  LOBBY_REFRESH_REQUESTED,
  REGISTER_SOCKET,
  TRACK_LOCATION
} from 'constants/eventConstants';
import { messageForRefusal } from 'constants/locationMessages';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import GlobalContext from 'contexts/GlobalContext';
import { LobbyProvider } from 'contexts/LobbyContext';
import SplashScreenContext from 'contexts/SplashScreenContext';
import { useFeatureFlag, useFeatureFlagsReady } from 'featureFlags';
import type { GeofencePath } from 'featureFlags/flags';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useActivityStripDispatch } from 'hooks/useActivityStripDispatch';
import {
  hasRenderableLobbyData,
  setLobbyReady,
  setSitAndGoList,
  setTournamentList
} from 'reducers/lobby';
import { PlayerGame, updatePlayerGames } from 'reducers/playerGames';
import { getLocationCheckStatus, verifyLoginLocation } from 'services/geofenceService';
import { getLobby } from 'services/lobbyService';
import { createTrailingRefresh, withJitter } from 'services/trailingRefresh';
import store from 'store';
import { getAccessToken, redirectToLogin } from 'utils/auth';
import { decompressData } from 'utils/functions';
import {
  GEOLOCATION_OPTIONS,
  isFreshLoginTransition,
  RESUME_RECHECK_THROTTLE_MS,
  shouldRetryLocationCheck,
  shouldRunGeolocation
} from 'utils/locationCheck';
import { LocationPermission, readLocationPermission } from 'utils/locationPermission';
import { isExitedTable } from 'utils/recentlyExitedTables';

import ForcePortrait from 'components/ForcePortrait/ForcePortrait';
import FreshDeskLink from 'components/FreshDeskLink';
import InvalidLocationModal from 'components/Location/InvalidLocationModal';
import SaloonLobby from 'components/SaloonLobby';
import { holdLobbyLanding, releaseLobbyLanding } from 'components/SaloonLobby/lobbyLanding';

import { deriveActiveTournamentTables } from './activeTournamentTables';
import { dispatchLobbyCashGames } from './lobbyCashGames';
import { isAtTableAndNotExited } from './playerAtTable';

/**
 * How long a socket must stay down before we ask whether location is why.
 *
 * Longer than CONNECTION_WARNING_DELAY_MS (1200ms) on purpose: the banner is
 * the right thing to show while the cause is genuinely unknown, and most failed
 * connects are blips that fix themselves well inside this window. The cost of
 * waiting is a few seconds of a wrong banner for a refused player; the cost of
 * not waiting is a request on every blip, fleet-wide.
 */
const GEO_REFUSAL_PROBE_DELAY_MS = 6_000;

/**
 * How long the member handshake may stay held before we give up on the verdict.
 *
 * Longer than the whole check can legitimately take: `waitForRadar` polls 8s for
 * the SDK and `runLoginGeofenceCheck` gives the vendor 45s after that, so a slow
 * but working check must never trip this. It is here for a chain that is never
 * RESUMED -- an app backgrounded mid-check has its timers suspended by the OS --
 * not for one that is merely slow.
 *
 * Expiry fails CLOSED. See the handler for why letting them through would buy
 * nothing.
 */
const GEO_HOLD_FAILSAFE_MS = 60_000;

/**
 * Hard floor between two re-checks that will reach the vendor.
 *
 * Not the primary control -- the permission-transition rule above it is, and it
 * is the better one, because it spends on a real change rather than on a clock.
 * This exists because the 2026-10-09 storm was caused by a WRONG BRANCH above
 * the throttle, not by a badly tuned throttle, so the only guard worth having
 * is one that survives the reasoning underneath it being wrong.
 *
 * Short enough that a player who genuinely just granted the permission is not
 * made to wait, long enough that nothing can turn into a per-second loop.
 */
const MIN_VENDOR_RECHECK_MS = 10_000;

// True once the lobby has loaded successfully at least once this session.
// Subsequent navigations back to the lobby skip the full branded splash and
// show a quick dark fade instead — avoids the cold-start animation replaying
// every time the player returns from a table.
let _lobbyLaunched = false;

const Lobby: React.FC<any> = () => {
  const dispatch = useAppDispatch();
  const _maintenance = useAppSelector((state) => state.maintenanceSchedule);
  const playerGames = useAppSelector((state) => state.playerGames);
  const guid = useAppSelector((state) => state.user.playerGUID);
  // Legacy's client-side bypass. Still populated; see the legacy branch of
  // the geo gate for why the legacy path must not run without it.
  const isRestrictedFlag = useAppSelector((state) => state.user.isRestrictedFlag);

  // The live guid, for the shadow measurement to tell a session that ENDED
  // from an effect re-run it should survive. See the abandoned() note below.
  const liveGuidRef = useRef(guid);
  liveGuidRef.current = guid;
  const lobbyReady = useAppSelector((state) => state.lobby.isLobbyReady);
  const [toastMsg, setToastMsg] = useState('');

  const { socket, setGameType, connectionStatus, setGeoHold } = useContext(SocketContext);
  const {
    isLocated,
    isLocationInvalid,
    setIsLocationInvalid,
    setInvalidLocationMsg,
    setIsLocated,
    committedGeoLegacy,
    setCommittedGeoLegacy,
    geoCheckPending,
    setGeoCheckPending
  } = useContext(GlobalContext);
  const { showSplash, hideSplash } = useContext(SplashScreenContext);

  // Which geolocation implementation this client runs.
  //
  // The server half of this switch is the SSM parameter /<stage>/geo/provider,
  // which decides whether `$connect` enforces. They must be moved in the order
  // documented in set-geo-enforcement-mode.yml. Off (the default, and what a
  // client sees until LaunchDarkly resolves) is the vendor path, which is the
  // only value safe under both server settings.
  const geofencePath = useFeatureFlag('geofencePath');
  // Whether LaunchDarkly has finished deciding for THIS player.
  //
  // Load-bearing, not a nicety. `useFeatureFlag` returns the default until the
  // SDK resolves, and the SDK only starts when `identity` appears -- which is
  // the same GOT_LOGIN that sets `guid` and fires the effect below. So the
  // effect always wins that race and always sees `false` on its first run.
  // Branching on it unguarded meant a player targeted for the legacy path ran
  // the vendor path first, failed, and then ran the legacy path when the flag
  // landed: both implementations on one login, a wasted vendor transaction, and
  // the contradictory logs QA reported. ADR-0015 requires exactly one path.
  //
  // `useFeatureFlagsReady` is bounded and identity-keyed: it settles on a real
  // evaluation, on SDK failure, or on its own ceiling timeout, so this can wait
  // for it without risking a permanent splash.
  const geoFlagsReady = useFeatureFlagsReady();

  // The path this login committed to. Pinned so a flag that changes LATER --
  // an operator flipping the kill switch mid-session, or a live `change:`
  // push -- cannot start the other implementation on top of the one already
  // running. A flag change takes effect on the player's next load, which is
  // the safe boundary ADR-0015 asks for.
  const geoPathRef = useRef<{ guid: string; path: GeofencePath } | null>(null);

  // Whether the WebSocket connect should WAIT for a location verdict.
  //
  // Only when the vendor path is on AND we have a logged-in player. Two things
  // would break otherwise, and both connect fine today:
  //   - guests (guid === '') never receive a verdict at all, so gating on one
  //     would leave them permanently unconnected and unable to browse;
  //   - the legacy GPS path reaches `isLocated` by a different route -- it
  //     emits TRACK_LOCATION over the socket, so the socket has to be open
  //     BEFORE the check can run. Holding the connect on a verdict would
  //     deadlock it: no socket, no check, no verdict, no socket.
  //
  // EN-9657 gated on `isLocated` unconditionally, which has exactly the guest
  // problem above. Deliberately not copied.
  //
  // Held until we KNOW the path. While LaunchDarkly is still deciding we have
  // not committed to either implementation, and the safe assumption is the
  // vendor one: releasing the socket early would show the lobby to someone we
  // may be about to refuse, which is the thing this gate exists to prevent.
  // Gated on the COMMITTED path, never the live flag. Until this login has
  // committed, `committedGeoLegacy` is null and the socket is held -- the safe
  // assumption, since releasing early would show the lobby to someone we may
  // be about to refuse. Reading the live flag here let the gate open the moment
  // an operator flipped it, even while a vendor verdict was pending or had
  // already refused the player.
  const geoGateActive = guid !== '' && committedGeoLegacy !== true;

  // True while a vendor verdict is outstanding.
  //
  // Without this the lobby paints first and the blocking screen arrives
  // afterwards, because the legacy action socket is NOT geo-gated: it connects
  // normally, `lobbyReady` flips, and `notReady` goes false while the vendor
  // call is still in flight. The player then sees the product for however long
  // Radar takes (up to SOFT_CHECK_TIMEOUT_MS, 45s) before being told they are
  // out of region. Showing an out-of-region player the lobby at all is the
  // thing the gate exists to prevent, so treat "no verdict yet" as not-ready
  // rather than as allowed.
  //
  // Starts false and is raised only when a check actually begins, so guests and
  // the legacy path are untouched.
  // Lives in GlobalContext now, not here. The connection banner and the socket
  // hold both need it, and neither is inside Lobby.
  const geoVerdictPending = geoCheckPending;

  /**
   * Re-open the location gate without a login.
   *
   * The gate effect below runs once per login because it returns early on
   * `isLocated` and nothing in its dependency list moves afterwards. That is
   * correct for the login it was written for and wrong for every later reason
   * the answer might change -- above all a location permission granted after
   * the fact, which on native happens in Settings, outside the app, and moves
   * no React state at all (EN-11114).
   *
   * Bumping this tick re-opens the gate. Paired with `setIsLocated(false)` by
   * the caller so the early return opens too; the tick is what makes the effect
   * fire when `isLocated` was already false, which is exactly the
   * modal-currently-showing case we most need to recover from.
   */
  const [geoRetryTick, setGeoRetryTick] = useState(0);
  const lastResumeCheckRef = useRef(0);
  /** Last re-check that actually reached the vendor; see MIN_VENDOR_RECHECK_MS. */
  const lastVendorRecheckRef = useRef(0);
  /**
   * What the OS last reported for the location permission.
   *
   * `null` means we have not looked yet, which is deliberately NOT treated as
   * "unchanged": the first read after a refusal is the one most likely to find
   * a permission the player just granted, so it must be allowed to spend a
   * lookup. Every read after that only spends one on an actual transition.
   */
  const lastPermissionRef = useRef<LocationPermission | null>(null);
  /**
   * Whether the pending run was triggered by a re-check rather than by login.
   *
   * It selects whether the run may reuse the stored verdict. A re-check exists
   * because something about the player's situation may have changed, and
   * `GET /location/status` happily hands back a refusal for up to
   * UNPASSED_REUSE_MS. Reusing it here would answer the new question with the
   * old answer and the re-check would do nothing at all.
   *
   * A ref rather than state: it is read inside the effect that the tick already
   * re-runs, so making it a dependency would only add a second render.
   */
  const geoRecheckRef = useRef(false);

  useEffect(() => {
    // Hold the connection until the check passes. ConnectionManager.initialize
    // is idempotent (guarded by initializedRef), so calling setGameType once
    // more after `isLocated` flips is safe. A player who fails never connects
    // at all, which is why they see the blocking screen instead of a socket
    // error, and why we are not opening a connection we intend to refuse.
    if (geoGateActive && !isLocated) return;
    setGameType('lobby');
    // Blank the lobby ONLY when there is genuinely nothing to show.
    //
    // This used to reset unconditionally on every mount, and the lobby is
    // unmounted while the player is at a table (SlideContainer) — so coming
    // back via "Go to Lobby" / the header logo / "+" threw away a perfectly
    // good, already-loaded lobby and dropped straight to `return null` below.
    // With nothing painted, what the player looked at was the flat #12100c
    // loading fill (FadeOverlay is that exact colour, as is the SlideContainer
    // wrapper) until the next snapshot arrived. LOBBY_DATA_RECEIVED is a
    // server PUSH on a ~1s cadence, not a request we can hurry, so that was up
    // to a second of brown on every return to the lobby (Marlon QA 8/24).
    //
    // The lists live in redux and nothing clears them on the way to a table,
    // so an in-session return has real content to paint immediately; the next
    // push replaces it in place. A cold start still blanks and still gets the
    // splash, because there is nothing to show yet.
    if (!hasRenderableLobbyData(store.getState().lobby)) dispatch(setLobbyReady(false));
  }, [dispatch, setGameType, geoGateActive, isLocated]);

  const [_playerGamesTournament, setPlayerGamesTournament] = useState<any[]>([]);
  const [_playerGamesCash, setPlayerGamesCash] = useState<any[]>([]);
  const [_isCashGameLoadingPanel, setIsCashGameLoadingPanel] = useState<boolean>(true);
  const [_isTournaLoadingPanel, setIsTournaLoadingPanel] = useState<boolean>(true);

  const { hasFilter, sortingField, sortingDirection } = useAppSelector((state) => state.lobby);
  // Sources used by the LOBBY_DATA_RECEIVED handler to merge the player's
  // active tournament tables into the YourTables strip AND into the
  // SubscribeToGameUpdates loop below. The per-mount `subscribedThisLobbyMount`
  // dedup guarantees each tournament gameID is subscribed at most ONCE per
  // Lobby mount — the previous "auto-subscribe on every playerTournament-
  // Details push" effects were removed because they ran at ~1 Hz and
  // collided with the cash-only dedup cleanup. Refs shadow the redux values
  // so the socket.on callback (a long-lived closure) always reads the latest
  // snapshot without adding these to the effect's dependency array (which
  // would tear down + re-register socket.on on every push).
  const playerTournamentDetails = useAppSelector(
    (state) => (state as any).lobby?.playerTournamentDetails as any[] | undefined
  );
  const sitAndGoList = useAppSelector(
    (state) => (state as any).lobby?.tournament?.sitAndGoList as any[] | undefined
  );
  const playerTournamentDetailsRef = useRef<any[]>([]);
  const sitAndGoListRef = useRef<any[]>([]);
  useEffect(() => {
    playerTournamentDetailsRef.current = playerTournamentDetails || [];
  }, [playerTournamentDetails]);
  useEffect(() => {
    sitAndGoListRef.current = sitAndGoList || [];
  }, [sitAndGoList]);

  useEffect(() => {
    const cashPlayerGames = playerGames.filter((data: PlayerGame) => data.tabletype === 's');
    const tournaPlayerGames = playerGames.filter(
      (data: PlayerGame) => data.tabletype === 'm' || data.tabletype === 't'
    );
    const dataTPanel = cashPlayerGames.map((data: PlayerGame) => ({
      card1: data.card1,
      card2: data.card2,
      card3: data.card3,
      card4: data.card4,
      card5: data.card5,
      gameInfo: { gameID: data.gameID, game: data.game, isPrivate: false },
      potDetail: {
        totalPot: data.pot,
        myTurn: data.myTurn,
        bet: data.bet,
        status: data.status,
        tableType: data?.tabletype,
        hand: data?.hand,
        tournamentStatus: data?.tournamentStatus
      }
    })) as any[];
    setPlayerGamesCash(dataTPanel);

    const dataTournaPanel = tournaPlayerGames.map((data: PlayerGame) => ({
      card1: data.card1,
      card2: data.card2,
      gameInfo: { gameID: data.gameID, game: data.game, isPrivate: false },
      potDetail: {
        totalPot: data.pot,
        myTurn: data.myTurn,
        bet: data.bet,
        status: data.status,
        tableType: data?.tabletype,
        hand: data?.hand,
        tournamentStatus: data?.tournamentStatus
      }
    })) as any[];
    setPlayerGamesTournament(dataTournaPanel);
    setTimeout(() => {
      setIsTournaLoadingPanel(false);
      setIsCashGameLoadingPanel(false);
    }, 2000);
  }, [playerGames]);

  // Per-session record of tables we've already auto-subscribed in this
  // Lobby mount. LOBBY_DATA_RECEIVED fires every ~1s while in the lobby and
  // the derived-from-seat auto-subscribe below was re-emitting for every
  // seated table on every tick. The lambda subscription TTL is 24h
  // (TTL.SUBSCRIPTION in shared/lib/dynamoHelpers.js), so once subscribed
  // we don't need to re-emit. Resets on Lobby unmount/remount (which
  // matches "new socket session" for our purposes).
  const subscribedThisLobbyMount = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!socket) return;

    // Register immediately if already connected, plus on future (re)connects
    if (socket.connected) {
      socket.emit(REGISTER_SOCKET, { page: 'lobby' });
    }

    socket.on(CONNECT, () => {
      socket.emit(REGISTER_SOCKET, { page: 'lobby' });
    });

    const handleLobbyData = (receivedCashGames: any) => {
      receivedCashGames = decompressData(receivedCashGames);
      // Render the lobby as soon as we know the member (guid) and have data.
      // We deliberately do NOT gate this on `isLocated` (geolocation): browsing
      // the lobby is allowed without a location fix — guests do exactly that —
      // and the Android WebView's navigator.geolocation frequently never calls
      // back, which used to leave members stuck on the splash forever. Play
      // stays gated regardless: the server refuses the socket for a player
      // whose login verdict says no, and InvalidLocationModal covers the
      // screen. Only browsing is unblocked.
      if (guid !== '' && receivedCashGames !== null && receivedCashGames !== undefined) {
        // Shared with GuestLobby: group cash games by seats→stakes and dispatch
        // into the Redux lobby store (see ./lobbyCashGames).
        const { rawTexasGames, rawOmahaGames } = dispatchLobbyCashGames(
          receivedCashGames,
          dispatch,
          { hasFilter, sortingField, sortingDirection }
        );

        // Derive active tables from lobby data by matching player GUID against
        // seat GUIDs. "Genuinely at the table" includes the away-states the
        // strip shows (standing up / left game / joining / sitting out) — see
        // isPlayerAtTable / ACTIVE_AT_TABLE_STATUSES.

        // Tag entries by the source array they came from so omaha tables are
        // never mis-labelled as texas downstream (App.tsx auto-resubscribe and
        // the engine ONLINE/SUBCRIBE events rely on this `game` value).
        //
        // Exclude recently-exited tables from BOTH the derived list AND the
        // state.playerGames dispatch. The engine's lobby cache can lag a tick
        // or two behind an unregister / leave — without this guard, a stale
        // "still seated" snapshot re-hydrates state.playerGames every ~1s
        // and the TableHeader chip (tablesFromPlayerGames branch) flickers
        // back on screen, undoing the SNG cleanup in TournamentDetailModal.
        const isSeatedAndNotJustLeft = (table: any) => isAtTableAndNotExited(table, guid);
        const activeCashTables = [
          ...(rawTexasGames || []).filter(isSeatedAndNotJustLeft).map((table: any) => ({
            gameID: table.gameID,
            game: 'texas',
            tabletype: table.tabletype || 's'
          })),
          ...(rawOmahaGames || []).filter(isSeatedAndNotJustLeft).map((table: any) => ({
            gameID: table.gameID,
            game: 'omaha',
            tabletype: table.tabletype || 's'
          }))
        ];

        // Merge in the player's active TOURNAMENT tables (MTT / SnG) derived
        // from state.lobby.playerTournamentDetails. LOBBY_DATA_RECEIVED only
        // carries cash rooms; without this the YourTables strip would miss
        // the tournament the player is playing right after a page refresh.
        // Reads via refs so this handler's closure always sees the latest
        // snapshot without re-registering socket.on every ~1s.
        const activeTournamentTables = deriveActiveTournamentTables(
          playerTournamentDetailsRef.current,
          sitAndGoListRef.current,
          isExitedTable
        );

        const activeTables = [...activeCashTables, ...activeTournamentTables];

        dispatch(updatePlayerGames(activeTables.length > 0 ? activeTables : [{}]));

        // Subscribe to each derived active table so state.tables populates
        // even when the engine's got_playerGames hasn't returned this table
        // (e.g., Redis player-games-{game}-{guid} cache miss for pre-fix
        // joins, or 20s freshness filter on idle texas tables — see
        // docs/investigations/20260512-omaha-tables-missing-after-refresh.md).
        // Without this, the chip shows from state.playerGames (this dispatch)
        // but /grid is empty because MultiTableGrid reads state.tables which
        // never gets populated without an active subscribe.
        //
        // We intentionally do NOT write these into localStorage.subscribedTables
        // so the engine-side unsubscribe diff in App.tsx doesn't fight us
        // (it would unsub any tracked gameID that's missing from a later
        // got_playerGames, causing a ping-pong loop).
        //
        // Dedup against subscribedThisLobbyMount: LOBBY_DATA_RECEIVED fires
        // every ~1s and re-emitting subscribe per-table per-tick was the
        // network spam the user observed (151+154 byte payloads at 1s).
        const seenInThisTick = new Set<string>();
        activeTables.forEach((g: any) => {
          if (!g.gameID) return;
          const gid = String(g.gameID);
          seenInThisTick.add(gid);
          if (isExitedTable(gid)) return;
          const game = g.game || 'texas';
          if (subscribedThisLobbyMount.current[gid] === game) return;
          socket.emit('SubscribeToGameUpdates', {
            gameID: gid,
            game,
            playerGUID: localStorage.getItem('playerGUID') || '',
            username: localStorage.getItem('playerName') || ''
          });
          subscribedThisLobbyMount.current[gid] = game;
        });

        // Drop dedup entries for tables that left the active set this tick
        // (player unregistered from SNG, left a cash table, etc.). Without
        // this, a legitimate rejoin within the same Lobby mount would be
        // silently skipped — and the SNG-after-unregister chip residue was
        // partly this: even though TournamentDetailModal cleared state.tables,
        // this dedup record was the only thing keeping a stale "we already
        // subscribed" promise alive across the unregister.
        Object.keys(subscribedThisLobbyMount.current).forEach((gid) => {
          if (!seenInThisTick.has(gid)) delete subscribedThisLobbyMount.current[gid];
        });

        dispatch(setLobbyReady(true));
      }
    };

    socket.on(LOBBY_DATA_RECEIVED, handleLobbyData);

    const abortController = new AbortController();
    const refreshLobby = createTrailingRefresh(
      () =>
        getLobby(abortController.signal)
          .then(handleLobbyData)
          .catch((error) => {
            // eslint-disable-next-line no-console
            if (error?.name !== 'AbortError') console.error('[Lobby] REST refresh failed:', error);
          }),
      abortController.signal
    );
    // Jittered: this signal reaches every lobby client at once, and without a
    // spread they'd all call refreshLobby in the same instant (see withJitter).
    const jitteredRefresh = withJitter(() => void refreshLobby());
    socket.on(LOBBY_REFRESH_REQUESTED, jitteredRefresh.trigger);
    void refreshLobby();

    // Named so the cleanup removes only this listener: SitGoResultWatcher keeps
    // its own on the same event while a table is on screen (EN-11109).
    const handleTournaments = (data: any) => {
      data = decompressData(data);
      // A payload without a response list is ignored, as SitGoResultWatcher does,
      // and a null entry is skipped, not read.
      if (!Array.isArray(data?.response)) return;
      const sitAndGo = data.response.filter((row: any) => row?.tabletype === 't');
      const tournaments = data.response.filter((row: any) => row?.tabletype === 'm');

      dispatch(setSitAndGoList(sitAndGo));
      dispatch(setTournamentList(tournaments));
    };
    socket.on(GOT_TOURNAMENTS, handleTournaments);

    socket.on(GOT_MULTIBAG_PRIZE, (data: any) => {
      data = decompressData(data);
      if (data.length > 0) {
        const filtered = data.filter(
          (d: any) => d.playerId === (guid === '' ? localStorage.getItem('playerGUID') : guid)
        );
        if (filtered.length > 0) {
          const totalPrize = filtered.reduce((acc: any, current: any) => acc + current.prize, 0);

          if (totalPrize > 0) {
            setToastMsg(
              `Congratulations! You've earned $${totalPrize} for your extra bag in the multi-flight tournament.`
            );
            setTimeout(() => setToastMsg(''), 6000);
          }
        }
      }
    });

    return () => {
      abortController.abort();
      jitteredRefresh.cancel();
      socket.off(GOT_TOURNAMENT_REBUY);
      socket.off(CONNECT);
      socket.off(GOT_TOURNAMENTS, handleTournaments);
      socket.off(GOT_MULTIBAG_PRIZE);
      socket.off(LOBBY_DATA_RECEIVED);
      socket.off(LOBBY_REFRESH_REQUESTED);
    };
  }, [socket, dispatch, hasFilter, sortingField, sortingDirection, guid]);

  // Activity strip (Tables / Players / In Play): polled from its own public
  // CDN document (see useActivityStripDispatch/useActivityStripFeed) rather
  // than a socket event. The legacy engine's activity-strip broadcaster was
  // removed some time ago (its event constant has since been removed from
  // this file too), so this had gone dark — this is the new, independent
  // source, and needs no socket connection at all.
  useActivityStripDispatch();

  // EN-11109: a Sit & Go landing armed on the way here waits while this page
  // loads; SaloonLobbyContainer, which takes it, mounts only once it is ready.
  useEffect(() => {
    holdLobbyLanding();
    return () => releaseLobbyLanding();
  }, []);

  // NOTE: the previous "auto-subscribe to registered MTT / SnG tables from the
  // lobby" effects were removed intentionally. They ran on every
  // playerTournamentDetails push (~1 Hz) and collided with LOBBY_DATA_RECEIVED's
  // dedup cleanup — tournament gameIDs were wiped from the dedup record each
  // tick, so SubscribeToGameUpdates fired every second for the same MTT/SnG
  // table (see the network log in the RCA). Their replacement is INLINE in the
  // LOBBY_DATA_RECEIVED handler above: `deriveActiveTournamentTables` folds
  // tournament rows into `activeTables`, which drives BOTH the
  // `dispatch(updatePlayerGames(...))` (display in "Your Tables") AND the
  // `SubscribeToGameUpdates` loop (feed) — sharing the same
  // `subscribedThisLobbyMount` dedup so each gameID emits at most once per
  // Lobby mount, not once per tick. On a page refresh the tournament chip
  // shows up immediately without spamming the wire.

  // Track guest→login transitions. A guest has guid === ''; a logged-in
  // player has a real playerGUID. When the transition happens (guest logs in),
  // wipe any location-modal state carried over from the guest phase — reported
  // bug: "fresh install → open app as guest → login → the 'attempt timed out'
  // modal prompts even though location is allowed". Any leftover isLocationInvalid
  // from a stray guest-phase event would render the modal instantly on login;
  // this reset guarantees the fresh login starts with a clean slate and only
  // the fresh server verdict can raise the modal from here.
  const prevGuidRef = useRef(guid);
  useEffect(() => {
    // Detection delegated to `isFreshLoginTransition` — unit-tested to fire
    // only on ''→non-empty and NOT on logout / same-guid re-render / guid-
    // swap between two logged-in states.
    if (isFreshLoginTransition(prevGuidRef.current, guid)) {
      setIsLocationInvalid(false);
      setInvalidLocationMsg('');
      setIsLocated(false);
    }
    prevGuidRef.current = guid;
  }, [guid, setIsLocationInvalid, setInvalidLocationMsg, setIsLocated]);

  /**
   * The LEGACY location check, restored verbatim from the pre-LexisNexis client
   * so the `ops.client-geofence-path` fallback behaves exactly as it did.
   *
   * Reads a position from the device and emits it over the socket; the engine
   * reverse-geocodes it, evaluates the Split.io `GeoFencing` treatment and
   * answers `gotLocation`, which App.tsx turns into the modal. Note the shape
   * of that contract: the socket must already be open, which is why the legacy
   * path cannot be gated at `$connect` and why `geoGateActive` excludes it.
   *
   * Not dead code. This is what the kill switch runs.
   */
  const geoFindMe = () => {
    // Visibility guard delegated to `shouldRunGeolocation` (utils/locationCheck)
    // so the "don't start a wall-clock timer while backgrounded" rule is
    // covered by unit tests. See locationCheck.test.ts.
    if (typeof document !== 'undefined' && !shouldRunGeolocation(document.visibilityState)) {
      return;
    }

    function success(position: any) {
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;
      const descopeSession = getAccessToken();

      if (!descopeSession) {
        redirectToLogin();
        return;
      }
      socket.emit(TRACK_LOCATION, {
        geoLocationError: false,
        jwt: descopeSession,
        latitude: latitude,
        longitude: longitude,
        playerGUID: guid,
        deviceID: localStorage.getItem('deviceID') === null ? '' : localStorage.getItem('deviceID')
      });
    }

    function error(err: any) {
      setIsLocationInvalid(true);
      if (err.code === 1) {
        setInvalidLocationMsg(
          "You've decided not to share your position, but it's OK. We won't ask you again."
        );
      } else if (err.code === 2) {
        setInvalidLocationMsg("The network is down or the positioning service can't be reached.");
      } else if (err.code === 3) {
        setInvalidLocationMsg('The attempt timed out before it could get the location data.');
      } else {
        setInvalidLocationMsg('Geolocation failed due to unknown error.');
      }
    }

    // Options centralised in utils/locationCheck — timeout 30s (cold-GPS
    // headroom) and maximumAge 0 (no cached positions for the region-gating
    // security check). Values are locked in by unit tests so a future
    // "let's try 10s again" change fails a test before it ships.
    const options = GEOLOCATION_OPTIONS;

    if (Capacitor.isNativePlatform()) {
      // In the Capacitor WebView, browser navigator.geolocation is unreliable —
      // there's no permission flow bridged to it, so getCurrentPosition just
      // times out ("attempt timed out"). Use the native Geolocation plugin,
      // which prompts for the runtime permission and reads the device GPS. Its
      // position/error shapes match the web API, so success/error are reused.
      (async () => {
        try {
          const { Geolocation } = await import('@capacitor/geolocation');
          let perm = await Geolocation.checkPermissions();
          if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
            perm = await Geolocation.requestPermissions();
          }
          if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
            error({ code: 1 }); // permission denied
            return;
          }
          const position = await Geolocation.getCurrentPosition(options);
          success(position);
        } catch {
          error({ code: 3 }); // unavailable / timed out
        }
      })();
      return;
    }

    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(success, error, options);
  };

  useEffect(() => {
    // The login-time geofence check (LexisNexis / Radar).
    //
    // Runs exactly once per login. There is no resume re-check: a player whose
    // situation changes reloads or restarts, the same as any other login-time
    // decision. The previous resume listener was inherited from the legacy GPS
    // path, where repeated polling WAS the design, and it dragged this check
    // along with it -- spending a vendor transaction on every foreground event,
    // unthrottled while the blocking screen was up.
    //
    // Deliberately does NOT short-circuit on isRestrictedFlag. The bypass is
    // evaluated server-side, and running the check for bypassed accounts too is
    // what gives observe mode a complete picture of how often the vendor
    // actually answers.
    // Clearing pending here matters: a logout while we are still waiting for
    // LaunchDarkly below would otherwise strand the splash on forever.
    if (guid === '') {
      setGeoCheckPending(false);
      // Retire the pin. Keyed by GUID alone it would survive a logout, so the
      // next login -- including the same player returning after the flag
      // changed -- would silently inherit the previous session's path.
      geoPathRef.current = null;
      setCommittedGeoLegacy(null);
      return undefined;
    }
    if (isLocated) return undefined;

    // Wait for the flag before choosing. Holding the splash is correct for
    // both paths here -- we have not started either yet, and showing the lobby
    // to someone we are about to refuse is the thing this gate exists to stop.
    if (!geoFlagsReady) {
      setGeoCheckPending(true);
      return undefined;
    }

    // Commit to one implementation for the rest of this login.
    if (!geoPathRef.current || geoPathRef.current.guid !== guid) {
      geoPathRef.current = { guid, path: geofencePath };
      // Publish whether LEGACY owns the player-facing outcome, which is what
      // the socket gate here and App's disconnect effect actually need to know.
      // Shadow counts as legacy for that purpose: the modal comes from
      // `gotLocation` over a healthy socket and the server refuses nobody, so
      // disconnecting would kill the lobby feed behind a client-side modal.
      setCommittedGeoLegacy(geofencePath !== 'lexisnexis');
    }
    const committedPath = geoPathRef.current.path;
    const runLegacy = committedPath !== 'lexisnexis';
    const runVendor = committedPath !== 'legacy';

    // Legacy: emit over the socket and let `gotLocation` decide.
    //
    // Deliberately CLEARS geoVerdictPending rather than holding it. That flag
    // holds the splash until a verdict lands, which is right for the vendor
    // path (it decides before the socket opens) and wrong here: the legacy
    // path needs the socket up to run at all, so holding the splash on it
    // would deadlock the lobby. The legacy path has always shown its modal
    // over a connected lobby.
    if (runLegacy) {
      setGeoCheckPending(false);

      // THE LEGACY BYPASS GATE, RESTORED. Dropping it was a regression.
      //
      // `main` ran the legacy check only for `isRestrictedFlag === 1` and sent
      // `isRestrictedFlag === 0` straight to `setIsLocated(true)`, so a
      // bypassed account never emitted TRACK_LOCATION at all. The comment above
      // argues the bypass is evaluated server-side, and that IS true of the
      // vendor path -- but it is not true of the legacy engine, which decides
      // the bypass and the geocode in the same handler and gets the order
      // wrong: `inbound.js:154` emits `status: 'success'` for a bypassed player
      // and then DOES NOT RETURN. It falls through to the reverse geocode, the
      // player_devices write and the Split.io treatment, any of which can emit
      // a second `gotLocation` carrying `status: 'error'`. The last emission
      // wins, and because `computeLocationEventUpdates` supplies no message for
      // `error`, the player gets InvalidLocationModal's default copy -- a
      // region warning -- having been granted a bypass moments earlier.
      //
      // Requiring `=== 1` also waits for the player record to hydrate. Firing
      // on `guid` alone can reach `findPlayerWithDevices` before the row is
      // there, which returns `'Player information not found.'` -- again
      // `status: 'error'`, again the same modal.
      //
      // The vendor path keeps running for bypassed accounts, deliberately: its
      // bypass really is server-side, and observe mode wants the full picture.
      if (isRestrictedFlag === 0) {
        setIsLocated(true);
      } else if (isRestrictedFlag === 1) {
        geoFindMe();
      }
      if (!runVendor) return undefined;
    }

    // HOLD THE MEMBER HANDSHAKE UNTIL THERE IS A VERDICT TO ADMIT ON.
    //
    // The app connects as a GUEST before login, so by the time this gate runs
    // the socket already exists and `setGameType` is idempotent -- withholding
    // it does nothing. Login then fires the guest-to-member upgrade, which
    // re-handshakes into `$connect` while the verdict is still being fetched.
    // With no stored verdict that is an automatic refusal, so the player gets a
    // connection error for the whole duration of their own location check. The
    // gate's own docblock claims a failing player "never connects at all"; the
    // guest-first flow is why that was never true.
    //
    // Deferring the upgrade is the only lever available: the socket cannot be
    // un-initialised, and holding reconnects generally would reach far more of
    // ConnectionManager than this is worth.
    // ONLY HOLD WHEN THE VENDOR VERDICT IS WHAT THE PLAYER IS WAITING ON.
    //
    // Under `legacy` or `legacy-shadow` legacy owns the outcome, and legacy
    // needs the socket UP to work at all -- it emits `TRACK_LOCATION` over it
    // and decides from `gotLocation`. Holding the member handshake there would
    // deadlock the very path that is supposed to answer.
    if (!runLegacy) setGeoHold(true);

    let cancelled = false;
    /**
     * Stop the failsafe once the check has produced an answer.
     *
     * Clearing this ONLY in the effect cleanup was a real defect, seen on test2
     * on 2026-10-08: a refused player was correctly shown "outside our supported
     * region", and sixty seconds later the failsafe fired and replaced it with
     * the vague "having trouble confirming your location". Nothing had gone
     * wrong; the answer had arrived and the timer was still armed.
     *
     * The cleanup was not enough because the effect only re-runs when one of its
     * dependencies moves. An ALLOWED player flips `isLocated`, so the effect
     * re-runs and the timer is torn down -- which is exactly why this hid. A
     * REFUSED player moves nothing, so the timer survives to overwrite a correct
     * and specific refusal with a worse one.
     */
    let failsafe: number | undefined;
    const stopFailsafe = () => {
      if (failsafe !== undefined) window.clearTimeout(failsafe);
      failsafe = undefined;
    };
    // Held only when the vendor verdict is the one the player is waiting on.
    // Under shadow it is not: legacy has already decided, and holding the
    // splash for a measurement nobody acts on would make an observation window
    // visible to every player in it.
    setGeoCheckPending(runLegacy ? false : true);

    (async () => {
      // Ask before buying. A Radar lookup is billed per call and the server
      // already holds a verdict it treats as current for 24 hours, so an app
      // open inside that window used to pay for an answer the server was going
      // to ignore. The probe is one DynamoDB point read against our own API.
      //
      // It returns the DECISION too, not just the yes/no, which is why a player
      // who was already refused lands on their blocking screen here without
      // buying a second lookup to be refused again.
      //
      // Not a gate. `getLocationCheckStatus` fails toward `required: true`, and
      // nothing a client does with this answer changes what `$connect` admits:
      // that is decided from the stored verdict, which only a vendor-signed
      // token can write.
      // A RE-CHECK MUST NOT BE ANSWERED OUT OF THE STORED VERDICT. The probe
      // reuses a non-passing verdict for UNPASSED_REUSE_MS (5 minutes), which
      // is a deliberate spend control against a refused player holding down
      // refresh. It is also exactly the window a player spends walking to
      // Settings, granting the permission and coming back, so consulting it on
      // a re-check would hand back the very refusal the re-check exists to
      // clear, buy nothing, and leave the player stuck for the same reason as
      // before. Re-checks are rare and throttled; paying for the lookup is the
      // correct trade here.
      const isRecheck = geoRecheckRef.current;
      geoRecheckRef.current = false;

      // A SHADOW MEASUREMENT MUST NOT BE CANCELLED BY THE ADMISSION IT DOES
      // NOT AFFECT.
      //
      // `cancelled` is set by this effect's cleanup, which runs on EVERY
      // re-run -- including the one legacy itself causes. Under shadow the
      // sequence is: geoFindMe emits, the engine answers `success` in ~300ms,
      // `gotLocation` sets `isLocated`, `isLocated` is in this effect's deps,
      // the effect tears down and re-runs into its `if (isLocated)` guard, and
      // the probe below resolves straight into `if (cancelled) return`. The
      // POST never happens.
      //
      // A REFUSAL does not do this: `computeLocationEventUpdates` sets
      // `isLocationInvalid`, which is NOT a dependency, so `isLocated` never
      // moves and the measurement completes. The result was that shadow
      // recorded only the players legacy REFUSED and silently dropped every
      // player it admitted -- a dataset biased to refusals, from a window whose
      // entire purpose is to characterise the normal population. Observed on
      // staging 2026-10-10: `locationVerify` had no log streams at all after
      // successful logins, while test2 recorded measurements only for refusals
      // and for the window when the engine was down and could not answer.
      //
      // Cancellation still matters for one thing -- not writing a measurement
      // for a session that has gone away -- so the shadow path checks THAT
      // instead. `cancelled` conflates it with an ordinary re-render; the guid
      // moving is the real signal, and it is what the cleanup's own comment
      // describes as the case it cares about.
      const abandoned = () => (runLegacy ? liveGuidRef.current !== guid : cancelled);

      const status = isRecheck ? { required: true as const } : await getLocationCheckStatus();
      if (abandoned()) return;

      const decision = status.required
        ? await verifyLoginLocation(guid, { shadow: runLegacy })
        : status.decision;
      if (abandoned()) return;

      // SHADOW IS WRITE-ONLY. Legacy has already decided what this player sees,
      // and the server refuses nobody while the provider dial is on `legacy`.
      // The verdict above exists to be recorded, so applying ANY of it here --
      // `isLocated`, `isLocationInvalid`, `invalidLocationMsg` -- would make an
      // observation window visible to the players in it, and would do it by
      // raising a blocking screen for a player legacy had admitted.
      //
      // The single-writer rule this keeps: under shadow the only writer of
      // those three is `gotLocation`, exactly as under plain `legacy`.
      if (runLegacy) {
        stopFailsafe();
        // A no-op: the hold is not taken on a path legacy owns. Passed as
        // not-admitted anyway, so nothing here can ever queue an upgrade.
        setGeoHold(false, false);
        return;
      }

      stopFailsafe();
      setGeoCheckPending(false);
      // Release WITH the answer. On an allow this performs the deferred
      // upgrade; on a refusal it drops it, because `reconnect()` schedules a
      // connect 500ms later that nothing cancels -- so releasing blindly
      // queued a member handshake the verdict had just refused, and App's
      // disconnect effect could not stop it.
      setGeoHold(false, decision.allowed);
      if (decision.allowed) {
        setIsLocated(true);
        setIsLocationInvalid(false);
        setInvalidLocationMsg('');
      } else {
        setIsLocationInvalid(true);
        // A refusal we could not conclude (our endpoint down, the server
        // failing closed on an internal lookup) must NOT tell the player they
        // are out of region: they may be perfectly allowed and we simply do
        // not know. messageForRefusal draws that line.
        setInvalidLocationMsg(messageForRefusal(decision.reason));
      }
    })()
      // Both calls resolve on every path they own (each converts its own
      // failures into a usable value), but this catch is what guarantees the
      // flag cannot latch. A pending flag that never clears is a permanent
      // splash screen, which is a worse outcome than briefly failing open.
      .catch(() => {
        if (cancelled) return;
        stopFailsafe();
        setGeoCheckPending(false);
        // Admitted by default here, deliberately. This catch only fires on an
        // unexpected throw, so no verdict exists either way -- and leaving an
        // otherwise-fine player on the guest socket with no member connection
        // is worse than a handshake the server may refuse. Matches
        // BACKEND_UNREACHABLE, which also fails toward letting them through.
        setGeoHold(false);
      });

    // FAILSAFE, AND IT FAILS CLOSED.
    //
    // Every path above resolves on its own, so this is for the case where the
    // chain is never RESUMED rather than never settled -- an app backgrounded
    // mid-check has its timers and fetches suspended by the OS, which is how
    // the EN-11114 reports arose in the first place.
    //
    // It does not let the player through. Releasing the hold without a verdict
    // would hand an unverified player a socket that `$connect` is going to
    // refuse anyway, so failing open buys nothing and costs the one guarantee
    // this gate exists for. It says plainly that we could not check instead:
    // `messageForRefusal` maps an unrecognised reason to the "having trouble
    // confirming your location" copy and never to the out-of-region one,
    // because we have established nothing about where this player is.
    //
    // Not terminal -- a verdict arriving afterwards still wins and clears it.
    failsafe = window.setTimeout(() => {
      if (cancelled) return;
      console.warn('[geofence] location check did not settle -- failing closed');
      setGeoCheckPending(false);
      setGeoHold(false, false);

      // SHADOW IS WRITE-ONLY, INCLUDING WHEN IT TIMES OUT.
      //
      // Under `legacy` or `legacy-shadow` legacy owns what the player sees and
      // the vendor result is a measurement nobody acts on. A stalled
      // measurement raising a blocking screen would make an observation window
      // visible to the players inside it, and would do it by blocking someone
      // legacy had already admitted. The stall still earns the warning line
      // above; it does not earn a modal.
      if (runLegacy) return;

      setIsLocationInvalid(true);
      setInvalidLocationMsg(messageForRefusal('check_did_not_settle'));
    }, GEO_HOLD_FAILSAFE_MS);

    // A logout or guid swap mid-flight must not apply a verdict belonging to
    // the previous session.
    return () => {
      cancelled = true;
      stopFailsafe();
      setGeoCheckPending(false);
      // A logout or guid swap must not leave the upgrade held for the next
      // session, which would strand it with no gate running to release it.
      // Not-admitted: no verdict was reached, and performing a member upgrade
      // for a session that is going away is never the right answer.
      setGeoHold(false, false);
    };
    // `geoFindMe` is deliberately NOT a dependency. It is re-created every
    // render and adding it would re-run this effect on every render, which for
    // the legacy branch means a fresh getCurrentPosition and a fresh
    // TRACK_LOCATION emission each time. The `isLocated` guard above is what
    // makes this run once per login, and that is the behaviour being preserved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `geofencePath` is deliberately NOT a dependency. Re-running on a flag
    // change is precisely the defect this fix removes; the value is read once,
    // through geoPathRef, at the moment the flag is ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLocated,
    guid,
    isRestrictedFlag,
    geoFlagsReady,
    geoRetryTick,
    setCommittedGeoLegacy,
    setIsLocated,
    setGeoCheckPending,
    setIsLocationInvalid,
    setInvalidLocationMsg
  ]);

  /**
   * Re-check location whenever the app comes back to the foreground.
   *
   * RESTORED FROM THE LEGACY PATH (commit 5d03d957c8), which carried this and
   * was the reason denied-then-granted recovered there. The LexisNexis port
   * dropped it on the reasoning that the check "was removed on its own merits",
   * and EN-11114 is what that cost: on native, granting a location permission
   * means leaving the app for Settings and coming back, which moves no React
   * state. Without this listener the gate never runs again, `$connect` keeps
   * refusing a verdict nobody rewrote, and the player sits behind a reconnect
   * banner with no way out short of killing the app.
   *
   * Deliberately does NOT clear `isLocationInvalid` itself. The modal closes
   * only when a fresh verdict says it should, so a still-out-of-region player
   * who tabs away and back keeps their blocking screen, as they must.
   *
   * Both listeners are registered because neither covers both platforms:
   * `visibilitychange` is the web and WebView signal, Capacitor
   * `appStateChange` is the native one, and on native the Settings round trip
   * is the only way to change the permission at all.
   */
  useEffect(() => {
    const retry = async () => {
      if (guid === '') return;

      // WHAT MATTERS IS WHETHER THIS RE-CHECK WILL CALL THE VENDOR, NOT WHICH
      // PATH OWNS THE PLAYER'S SCREEN.
      //
      // `committedGeoLegacy` is true for BOTH `legacy` and `legacy-shadow`,
      // because it answers "does legacy own what the player sees". Branching on
      // it here gave `legacy-shadow` legacy's free-re-check rule -- which
      // bypasses the throttle entirely whenever the modal is up -- while shadow
      // still runs the vendor and POSTs the result on every single one.
      //
      // That is not theoretical. On test2, 2026-10-09 12:58:31 to 12:59:10,
      // this produced 164 `/location/verify` calls in forty seconds, roughly
      // four a second, against 2 calls to `/location/status` -- the giveaway,
      // since only a re-check skips the status probe. A blocked shadow player
      // had no rate limit of any kind.
      //
      // So gate on `runVendor`, which is the thing that actually costs money.
      // Only plain `legacy` gets the free rule, because only plain `legacy`
      // makes no vendor call.
      const vendorFree = geoPathRef.current?.path === 'legacy';
      if (vendorFree) {
        const now = Date.now();
        if (
          !shouldRetryLocationCheck({
            guid,
            isLocationInvalid,
            now,
            lastCheckAt: lastResumeCheckRef.current,
            throttleMs: RESUME_RECHECK_THROTTLE_MS
          })
        ) {
          return;
        }
        lastResumeCheckRef.current = now;
        geoRecheckRef.current = true;
        setIsLocated(false);
        setGeoRetryTick((t) => t + 1);
        return;
      }

      // ONLY RECOVER, NEVER RE-POLL. A player who is not blocked has nothing to
      // recover from: `$connect` enforces on the login verdict for its full
      // TTL, so re-confirming it on every foreground buys an answer the server
      // already holds and will not act on. Mid-session movement is deliberately
      // NOT caught here; that is the stated trade-off of login-only checking
      // (see geofenceVerify.ts) and changing it is not this fix's job.
      if (!isLocationInvalid) return;

      // A FLOOR THAT DOES NOT DEPEND ON THE REASONING BELOW BEING RIGHT.
      //
      // The permission-transition rule is what SHOULD stop repeats, and it is
      // the better control because it spends on a real change rather than on a
      // clock. But the storm above happened because a branch above this one was
      // wrong, not because the throttle was badly tuned -- so the only useful
      // guard is one that holds whatever the logic underneath decides. A
      // mistake here costs a player a few seconds; the absence of one costs a
      // vendor call several times a second.
      if (Date.now() - lastVendorRecheckRef.current < MIN_VENDOR_RECHECK_MS) return;

      // ONE RETURN TO THE APP FIRES TWO SIGNALS -- `visibilitychange` and
      // Capacitor `appStateChange` -- and both reach here. They do not both
      // spend: `lastPermissionRef` is read and written between the await and
      // the commit below, so the first caller records the permission and the
      // second sees it unchanged and returns. A separate synchronous claim was
      // added here and removed again once it proved unreachable; do not
      // reintroduce one without a test that fails without it.

      // ASK THE OS WHAT CHANGED, RATHER THAN GUESSING FROM THE CLOCK.
      //
      // A foreground event on its own says nothing about whether this player's
      // situation is any different from the one that refused them a moment ago.
      // Re-checking on it anyway means buying a lookup every time a blocked
      // player switches apps, and the only defence available without this read
      // is a throttle, which is a guess: too short and it is an open tap, too
      // long and someone who just fixed the actual problem is made to wait for
      // no reason.
      //
      // The permission state is the real signal, and it is the one thing that
      // actually changed in the EN-11114 report: the player left for Settings
      // precisely to change it. A transition out of `denied` can only happen
      // once per grant, so it cannot be spammed, which is what lets the lookup
      // be spent immediately rather than rationed.
      const permission = await readLocationPermission();
      const previous = lastPermissionRef.current;
      lastPermissionRef.current = permission;

      if (permission === 'denied') {
        // Nothing has changed and nothing can: the vendor cannot produce a
        // usable verdict without the permission, so a lookup here is spend with
        // a known-useless result. The player keeps the screen they are already
        // looking at.
        return;
      }

      // `unknown` means the platform would not tell us (Permissions API absent,
      // or it does not support the geolocation name). Fall back to the clock,
      // because the alternative is never recovering on those platforms. This is
      // the only branch where the throttle still decides anything.
      if (permission === 'unknown') {
        const now = Date.now();
        if (now - lastResumeCheckRef.current < RESUME_RECHECK_THROTTLE_MS) return;
        lastResumeCheckRef.current = now;
      } else if (previous !== null && previous === permission) {
        // Readable, and the same as last time we looked. Whatever is blocking
        // this player, it is not the permission, so a lookup would buy the same
        // refusal again.
        return;
      }

      // The floor is stamped HERE, where we commit to spending, not earlier. A
      // read that came back `denied` spends nothing, and burning the floor on
      // it would throttle out the grant that follows -- which is the recovery
      // this whole path exists for.
      lastVendorRecheckRef.current = Date.now();
      // Mark the run BEFORE re-opening the gate, so the effect sees it on the
      // same pass. This is what makes the re-check bypass the stored verdict.
      geoRecheckRef.current = true;
      setIsLocated(false);
      setGeoRetryTick((t) => t + 1);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void retry();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    let removeCapacitorListener: (() => void) | undefined;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', (state: { isActive: boolean }) => {
          if (state.isActive) void retry();
        });
        removeCapacitorListener = () => handle.remove();
      } catch {
        // Web build, or Capacitor unavailable. `visibilitychange` covers it.
      }
    })();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      removeCapacitorListener?.();
    };
  }, [guid, isLocationInvalid, committedGeoLegacy, setIsLocated]);

  /**
   * Name a socket that will not come up, when the reason is a location refusal.
   *
   * A `$connect` geofence refusal arrives as a failed WebSocket handshake and
   * nothing else. There is no body the page can read, so the client's entire
   * knowledge of the event is "the socket did not open", which is also what a
   * dead network looks like. Left alone it renders as the reconnect banner,
   * telling a refused player on a working connection that they have lost their
   * internet (EN-11114).
   *
   * This asks the one question that can tell the two apart, and it asks it of
   * our own API rather than the vendor: one DynamoDB point read, no lookup
   * billed.
   *
   * DELIBERATELY NARROW. It acts ONLY on a stored verdict that says no, and it
   * does not touch `isLocated` or `geoVerdictPending`, so it cannot put the
   * lobby behind a splash screen during an ordinary outage. The two cases it
   * stays out of:
   *
   *   `required: true` means the server has no verdict to report, which is the
   *   `no_login_verdict` refusal. Nothing is known here, so nothing is claimed;
   *   the resume re-check above is what rewrites a missing verdict.
   *
   *   A genuinely offline device cannot reach this endpoint anyway, and
   *   `getLocationCheckStatus` fails toward `required: true`, so an outage
   *   leaves the banner exactly as it is today.
   */
  useEffect(() => {
    if (guid === '') return undefined;
    // Already blocking. Re-asking would only cost a read to confirm what the
    // screen in front of the player already says.
    if (isLocationInvalid) return undefined;
    if (connectionStatus === 'connected' || connectionStatus === 'connecting') return undefined;

    let cancelled = false;
    // Waited out rather than asked immediately: most failed connects are
    // ordinary blips that resolve on the next attempt, and probing each one
    // would add a request per blip to answer a question that is almost always
    // "no". The delay is longer than CONNECTION_WARNING_DELAY_MS, so a refused
    // player may see the banner briefly before the region screen replaces it.
    // That is the honest ordering: we cannot know it was geo until we ask.
    const timer = window.setTimeout(() => {
      (async () => {
        if (!navigator.onLine) return;
        const status = await getLocationCheckStatus();
        if (cancelled || status.required || status.decision.allowed) return;
        setIsLocationInvalid(true);
        setInvalidLocationMsg(messageForRefusal(status.decision.reason));
      })().catch(() => {
        // Fail quiet. The banner stays, which is the current behaviour and the
        // safe one: claiming a location refusal we could not confirm would put
        // a blocking screen in front of someone whose socket is merely down.
      });
    }, GEO_REFUSAL_PROBE_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [connectionStatus, guid, isLocationInvalid, setIsLocationInvalid, setInvalidLocationMsg]);

  // `|| geoVerdictPending` holds the splash while the gate is undecided. The
  // existing clause stays as-is: once isLocationInvalid is true the modal owns
  // the screen and the splash must get out of its way.
  const notReady = ((!socket?.connected || !lobbyReady) && !isLocationInvalid) || geoVerdictPending;

  // Show the shared splash (via SplashScreenContext, see its docblock) while
  // socket/lobby data are still loading — the full branded splash on the
  // first-ever lobby load this session, the quicker FadeOverlay on any later
  // one (e.g. a brief reconnect).
  useEffect(() => {
    if (!notReady) return undefined;
    showSplash('lobbyLoading', _lobbyLaunched ? 'light' : 'full');
    return () => hideSplash('lobbyLoading');
  }, [notReady, showSplash, hideSplash]);

  if (notReady) {
    return null;
  }

  // Mark lobby as launched so future navigations use the lighter fade overlay.
  _lobbyLaunched = true;

  return (
    <LobbyProvider>
      {/* Mobile lobby is portrait-only — locked to portrait even in landscape. */}
      <ForcePortrait>
        <SaloonLobby />

        {toastMsg && (
          <div
            style={{
              position: 'fixed',
              bottom: 80,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(39, 174, 96, 0.95)',
              color: '#fff',
              padding: '10px 20px',
              borderRadius: '10px',
              fontSize: '0.8rem',
              fontWeight: 600,
              zIndex: 9999,
              maxWidth: '90vw',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
            }}>
            {toastMsg}
          </div>
        )}

        <InvalidLocationModal />
        <FreshDeskLink />
      </ForcePortrait>
    </LobbyProvider>
  );
};

export default Lobby;
